import { useState, useEffect, useCallback } from 'react';
import { vendorCostAnalysisApi } from '../services/vendorCostAnalysisApi';
import type { VCAFilterOptions, VCAFilters } from '../types/vendorCostAnalysis';

const STORAGE_KEY = 'vca-filters';

function getYtdDefaults(): { dateFrom: string; dateTo: string } {
    const now = new Date();
    const year = now.getFullYear();
    const lastCompleteMonthEnd = new Date(year, now.getMonth(), 0);
    const m = String(lastCompleteMonthEnd.getMonth() + 1).padStart(2, '0');
    const d = String(lastCompleteMonthEnd.getDate()).padStart(2, '0');
    return {
        dateFrom: `${year}-01-01`,
        dateTo: `${year}-${m}-${d}`,
    };
}

interface LegacyStored {
    company?: string;
    dateFrom?: string;
    dateTo?: string;
    vendorID?: string;          // pre-multiselect single value
    vendorIDs?: string[];
    groupVendorIDs?: string[];
    categoryCode?: string;
}

function loadSavedFilters(): Partial<VCAFilters> {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return {};
        const raw = JSON.parse(stored) as LegacyStored;
        // Migrate legacy single vendorID → vendorIDs array.
        const vendorIDs = Array.isArray(raw.vendorIDs) && raw.vendorIDs.length > 0
            ? raw.vendorIDs
            : raw.vendorID ? [raw.vendorID] : [];
        const groupVendorIDs = Array.isArray(raw.groupVendorIDs) ? raw.groupVendorIDs : [];
        return {
            company: raw.company,
            dateFrom: raw.dateFrom,
            dateTo: raw.dateTo,
            vendorIDs,
            groupVendorIDs,
            categoryCode: raw.categoryCode,
        };
    } catch { return {}; }
}

function saveFilters(filters: VCAFilters) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            company: filters.company,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            vendorIDs: filters.vendorIDs,
            groupVendorIDs: filters.groupVendorIDs,
            categoryCode: filters.categoryCode,
        }));
    } catch { /* ignore */ }
}

export function useVendorCostAnalysis() {
    const ytd = getYtdDefaults();
    const saved = loadSavedFilters();

    const [filterOptions, setFilterOptions] = useState<VCAFilterOptions>({
        companies: [],
        dateRange: { min: ytd.dateFrom, max: ytd.dateTo },
        vendors: [],
        categories: [],
        accounts: [],
        vendorGroups: [],
    });
    const [filters, setFilters] = useState<VCAFilters>({
        company: saved.company || '',
        dateFrom: saved.dateFrom || ytd.dateFrom,
        dateTo: saved.dateTo || ytd.dateTo,
        vendorIDs: saved.vendorIDs || [],
        groupVendorIDs: saved.groupVendorIDs || [],
        categoryCode: saved.categoryCode || '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        vendorCostAnalysisApi.getFilters()
            .then(opts => setFilterOptions(opts))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        saveFilters(filters);
    }, [filters]);

    const updateFilters = useCallback((updates: Partial<VCAFilters>) => {
        setFilters(prev => ({ ...prev, ...updates }));
    }, []);

    const resetFilters = useCallback(() => {
        const ytdDates = getYtdDefaults();
        setFilters({
            company: '',
            dateFrom: ytdDates.dateFrom,
            dateTo: ytdDates.dateTo,
            vendorIDs: [],
            groupVendorIDs: [],
            categoryCode: '',
        });
    }, []);

    return {
        filterOptions,
        filters,
        updateFilters,
        resetFilters,
        loading,
        error,
    };
}
